import React from 'react';
import ReactDOMServer from 'react-dom/server.browser';
import { WeightAndBalanceManifest } from '../../calendar/weight-and-balance/weight--and-balance-manifest/weight-and-balance-manifest';
import { currentUserFormats } from '../../common/user-formats/userFormats.js';

/**
 * Month-local replacement for the legacy `getWeightAndBalanceManifest()`
 * (`weight-and-balance/weight--and-balance-manifest/weight-and-balance-manifest.helpers.js`).
 *
 * Differences vs legacy:
 *   - NO Redux dispatch — caller passes the data already fetched via Symfony.
 *   - NO `window.location.reload()` ID-mismatch trap.
 *   - Pure props-driven render to a static HTML string suitable for
 *     `PrinterService.print(() => ...)`.
 *
 * Imports the manifest React tree directly. Pre-coding audit (epic
 * `32ce13b7`) confirmed `weight-and-balance-manifest.js` + its 14
 * `components/*.js` siblings are pure-props-driven (no `connect()`,
 * no `useSelector`, no `useDispatch`, no `react-redux` imports). Do NOT
 * import `weight-and-balance-manifest.helpers.js` — that's the file we
 * replace.
 *
 * @param {Object} ctx
 * @param {Object} ctx.event
 * @param {Object} ctx.weightAndBalance  Payload from `CalendarApi.getWeightAndBalance`.
 * @param {Object} ctx.user
 * @param {string} ctx.timezone
 * @param {Object} ctx.activityTypes     `{[id]: ActivityType}` map.
 * @param {Object} ctx.resources
 * @param {Object} ctx.organization      Has `organizationSettings.reverseManifestSeatNumbers`.
 * @param {Object} [ctx.formats]         User date/time formats; defaults to the current shared ones.
 * @returns {Promise<string>} HTML manifest body, ready for printing.
 */
export async function buildWeightBalanceManifest({
	event,
	weightAndBalance,
	user,
	timezone,
	activityTypes,
	resources,
	organization,
	formats = currentUserFormats()
}) {
	// Envelope image resolution strategy:
	//   - Empty `image` → skip entirely.
	//   - Same-origin image → fetch + blob URL (legacy behavior).
	//   - Cross-origin image (e.g. local dev → dev.holdshort.com storage) →
	//     skip the fetch (CORS blocks it) and pass the absolute URL directly;
	//     the print iframe loads it via `<img src>` without CORS restriction.
	//   - 404 or other failure → undefined → manifest renders without image.
	let imageBlobUrl;
	const rawImage =
		typeof weightAndBalance?.image === 'string' ? weightAndBalance.image.trim() : '';
	if (rawImage !== '') {
		let isSameOrigin = false;
		try {
			const u = new URL(rawImage, window.location.href);
			isSameOrigin = u.origin === window.location.origin;
		} catch {
			isSameOrigin = false;
		}
		if (isSameOrigin) {
			try {
				const response = await fetch(rawImage);
				if (response.ok) {
					const blob = await response.blob();
					imageBlobUrl = URL.createObjectURL(blob);
				}
			} catch {
				imageBlobUrl = undefined;
			}
		} else {
			// Cross-origin: hand the print iframe the absolute URL directly.
			imageBlobUrl = rawImage;
		}
	}
	// The live captured envelope chart is a `data:image/png;base64,...` URL —
	// not same-origin, so it needs NO fetch; hand it straight to `<img src>`.
	// Rendered as a SEPARATE image (below the static one) so a client's uploaded
	// static image — which may carry extra data — is never dropped.
	const envelopeImageBlob =
		typeof weightAndBalance?.envelopeImage === 'string' && weightAndBalance.envelopeImage
			? weightAndBalance.envelopeImage
			: undefined;
	const reverseManifestSeatNumbers =
		organization?.organizationSettings?.reverseManifestSeatNumbers === '1';
	return ReactDOMServer.renderToStaticMarkup(
		<WeightAndBalanceManifest
			event={event}
			resources={resources}
			weightAndBalance={weightAndBalance}
			user={user}
			timezone={timezone}
			activityTypes={activityTypes}
			reverseManifestSeatNumbers={reverseManifestSeatNumbers}
			imageBlob={imageBlobUrl}
			envelopeImageBlob={envelopeImageBlob}
			formats={formats}
		/>
	);
}
